import {ClickAwayListener, Popper} from '@mui/material';
import {clamp} from 'lodash';
import React, {useState, useRef} from 'react';
import classNames from 'classnames';
import {useField} from 'formik';
import {useLocation} from 'react-router-dom';

import {useMediaQuery} from 'src/hooks/useMediaQuery';

import {gradeTypeFromText} from 'shared/portfolio/utils';
import GradePicker from './grade-picker';

interface Props {
  quarterFinalGrade?: boolean;
  'aria-labelledby'?: string;
}

const GradeForm = ({quarterFinalGrade, 'aria-labelledby': ariaLabelledby}: Props) => {
  const mq = useMediaQuery();
  const location = useLocation();
  const gradeButtonRef = useRef(null);
  const inputRef = useRef(null);
  const gradePopoverRef = useRef(null);
  const [gradeInput, , gradeInputHelpers] = useField('grade.gradeInput');
  const [gradeType, , gradeTypeHelpers] = useField('grade.gradeType');
  const [showPopover, setShowPopover] = useState(false);
  const inModal = location.pathname.includes('/course/new');

  const gradeButtonOnClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowPopover(true);
  };

  const gradeInputOnChange = (e) => {
    let val = e.target.value.toString().toUpperCase();
    const type = gradeTypeFromText(val, gradeType.value);

    if (type !== undefined) {
      val = type === 'N' ? clamp(parseInt(val, 10), 0, 100) : val;
      gradeInputHelpers.setValue(val.toString());
      gradeTypeHelpers.setValue(type);
    } else if (val === '') {
      gradeInputHelpers.setValue(val);
    }
  };

  const ariaProps = {
    'aria-describedby': 'portfolio-courses-grade-form-legend',
    ...(ariaLabelledby && {'aria-labelledby': ariaLabelledby}),
  };

  return (
    <div className={`course-grade-form ${quarterFinalGrade ? 'quarter-final-grade' : ''}`}>
      <span id="portfolio-courses-grade-form-legend" style={{display: 'none'}}>
        Grades can be a letter grade, a percentage, P for Pass, F for Fail, W for Withdraw, or I for
        Incomplete
      </span>
      {mq.XS ? (
        <div className={classNames('course-grade-form-input', {active: showPopover})}>
          <button
            ref={gradeButtonRef}
            className="portfolio-grade-score-button"
            onClick={gradeButtonOnClick}
            {...gradeInput}
            {...ariaProps}
          >
            {gradeInput.value}
          </button>
        </div>
      ) : (
        <input
          className={classNames('course-grade-form-input', {active: showPopover})}
          type="text"
          ref={inputRef}
          {...gradeInput}
          onChange={gradeInputOnChange}
          onFocus={gradeButtonOnClick}
          onClick={gradeButtonOnClick}
          {...ariaProps}
        />
      )}
      <ClickAwayListener onClickAway={() => setShowPopover(false)}>
        <Popper
          open={showPopover}
          anchorEl={mq.XS ? gradeButtonRef.current : inputRef.current}
          style={{zIndex: 9003}}
          {...(mq.MS_AND_DOWN && inModal && {placement: 'top'})}
        >
          <div
            className={classNames('course-grade-popover', {
              '--top': inModal,
            })}
            ref={gradePopoverRef}
          >
            <div className="course-grade-popover-outer-triangle">
              <div className="course-grade-popover-inner-triangle"></div>
            </div>
            <div className="course-grade-popover-content">
              <GradePicker closePopover={() => setShowPopover(false)} />
            </div>
          </div>
        </Popper>
      </ClickAwayListener>
    </div>
  );
};

export default GradeForm;
